import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import api from '../../services/api';

const EmailVerificationPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const code = searchParams.get('code');
  const lang = searchParams.get('lang');
  
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasVerified, setHasVerified] = useState(false);
  const [hasPendingRegistration, setHasPendingRegistration] = useState(false);

  useEffect(() => {
    // Set language from URL parameter if provided
    if (lang && (lang === 'en' || lang === 'it')) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (hasVerified) return;
    
    const verifyEmail = async () => {
      try {
        setVerifying(true);
        setError(null);
        
        // Check for pending registration before verifying
        const pendingRegistration = localStorage.getItem('pendingClubRegistration');
        if (pendingRegistration) {
          setHasPendingRegistration(true);
        }
        
        // The backend returns HTML, but we'll make the request anyway
        await api.get(`/verify-email/${userId}`, {
          params: { 
            code,
            lang: lang || i18n.language 
          },
        });
        
        setVerified(true);
        setHasVerified(true);
      } catch (err: any) {
        console.error('Verification failed:', err);
        setHasVerified(true);
        
        if (err.response?.status === 400) {
          setError(t('emailVerification.errors.invalidCode'));
        } else if (err.response?.status === 404) {
          setError(t('emailVerification.errors.userNotFound'));
        } else {
          setError(t('emailVerification.errors.verificationFailed'));
        }
      } finally {
        setVerifying(false);
      }
    };
    
    if (userId && code) {
      verifyEmail();
    } else {
      setError(t('emailVerification.errors.invalidLink'));
      setVerifying(false);
      setHasVerified(true);
    }
  }, [userId, code, lang, t, hasVerified]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {verifying ? (
          <Box>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>
              {t('emailVerification.verifying.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('emailVerification.verifying.message')}
            </Typography>
          </Box>
        ) : verified ? (
          <Box>
            <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 3 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              {t('emailVerification.success.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {hasPendingRegistration 
                ? t('emailVerification.success.messageWithRegistration')
                : t('emailVerification.success.message')
              }
            </Typography>
            
            {hasPendingRegistration ? (
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>{t('emailVerification.success.registrationContinue.title')}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('emailVerification.success.registrationContinue.message')}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>{t('emailVerification.success.nextSteps.title')}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('emailVerification.success.nextSteps.step1')}<br />
                  {t('emailVerification.success.nextSteps.step2')} <strong>admin@hikeandfly.app</strong><br />
                  {t('emailVerification.success.nextSteps.step3')}<br />
                  {t('emailVerification.success.nextSteps.step4')}
                </Typography>
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => {
                  // Check if there was a pending club registration for this user
                  const pendingRegistration = localStorage.getItem('pendingClubRegistration');
                  if (pendingRegistration) {
                    try {
                      const registrationData = JSON.parse(pendingRegistration);
                      // Update the registration context to indicate email is now verified
                      const updatedData = {
                        ...registrationData,
                        step: 'email_verified',
                        verifiedAt: Date.now()
                      };
                      localStorage.setItem('pendingClubRegistration', JSON.stringify(updatedData));
                    } catch (e) {
                      // Invalid data, clear it
                      localStorage.removeItem('pendingClubRegistration');
                    }
                  }
                  navigate('/');
                }}
                sx={{
                  background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
                  },
                }}
              >
                {t('emailVerification.success.goToLogin')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.location.href = 'mailto:admin@hikeandfly.app'}
              >
                {t('emailVerification.success.contactAdmin')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 3 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              {t('emailVerification.failed.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error || t('emailVerification.failed.message')}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/register')}
              >
                {t('emailVerification.failed.registerAgain')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
              >
                {t('emailVerification.failed.goToLogin')}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
      
      {!verifying && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('emailVerification.support.havingIssues')}{' '}
            <a href="mailto:support@hikeandfly.app" style={{ color: '#3388ff' }}>
              support@hikeandfly.app
            </a>
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default EmailVerificationPage;